import React from 'react';
import { Lock, Leaf } from 'lucide-react';

const HeroSection = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen bg-[#0B1F17] overflow-hidden pt-20"
    >
      {/* Ambient glow effects */}
      <div className="absolute top-20 right-1/4 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-[150px] animate-ambient-glow" />
      <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#95B8A2]/10 rounded-full blur-[120px] animate-ambient-glow" style={{ animationDelay: '2s' }} />
      
      <div className="container-custom relative z-10">
        <div className="flex flex-col lg:flex-row items-center min-h-[calc(100vh-80px)] py-12 lg:py-20 gap-12 lg:gap-8">
          {/* Left Content - 60% */}
          <div className="w-full lg:w-[60%] space-y-8">
            {/* Eyebrow */}
            <p
              data-testid="hero-eyebrow"
              className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#D4AF37] animate-fade-in-up"
            >
              The Future of Personal Finance is Here
            </p>

            {/* Main Headline */}
            <h1
              data-testid="hero-headline"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#FAFAF7] leading-[1.1] tracking-tight animate-fade-in-up"
              style={{ animationDelay: '0.1s' }}
            >
              Stop Tracking
              <br />
              Your Money.
              <br />
              <span className="italic text-[#D4AF37]">Start Transforming It.</span>
            </h1>

            {/* Subheadline */}
            <p
              data-testid="hero-subheadline"
              className="font-body text-lg sm:text-xl text-[#FAFAF7]/70 leading-relaxed max-w-xl animate-fade-in-up"
              style={{ animationDelay: '0.2s' }}
            >
              Findell is the first financial wellness app that doesn't just track your money — 
              it teaches you how to think about it. Meet Vera, your personal AI financial coach.
            </p>

            {/* App Store Badges - Coming Soon */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div
                data-testid="hero-appstore-button"
                className="relative inline-block opacity-90"
              >
                <img 
                  src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" 
                  alt="Coming soon to App Store" 
                  className="h-12 sm:h-14 grayscale-[30%]"
                />
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0B1F17] text-[10px] font-body font-bold px-2 py-0.5 rounded-full shadow-lg">
                  SOON
                </span>
              </div>
              <div
                data-testid="hero-googleplay-button"
                className="relative inline-block opacity-90"
              >
                <img 
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" 
                  alt="Coming soon to Google Play" 
                  className="h-12 sm:h-14 grayscale-[30%]"
                />
                <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#0B1F17] text-[10px] font-body font-bold px-2 py-0.5 rounded-full shadow-lg">
                  SOON
                </span>
              </div>
            </div>

            {/* Secondary CTA */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <a
                href="#features"
                data-testid="hero-watch-button"
                className="inline-flex items-center gap-2 font-body font-semibold text-[#D4AF37] hover:text-[#e6c455] transition-colors"
              >
                See How It Works →
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 pt-2 text-sm text-[#FAFAF7]/60 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
              <div data-testid="trust-free" className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body">Free forever plan</span>
              </div>
              <div data-testid="trust-security" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body">Bank-level security</span>
              </div>
              <div data-testid="trust-plaid" className="flex items-center gap-2">
                <span className="font-body">Plaid powered</span>
              </div>
            </div>
          </div>

          {/* Right Content - 40% Phone Mockup */}
          <div className="w-full lg:w-[40%] flex justify-center lg:justify-end">
            <div
              data-testid="hero-phone-mockup"
              className="relative animate-float"
            >
              {/* Phone Frame - Heritage Style with Gold Glow */}
              <div className="relative w-[280px] sm:w-[320px] h-[560px] sm:h-[640px] bg-gradient-to-b from-[#1B4332] to-[#0B1F17] rounded-[40px] p-3 shadow-2xl animate-gold-glow">
                <div className="w-full h-full bg-[#0B1F17] rounded-[32px] overflow-hidden relative border border-[#D4AF37]/20">
                  {/* Status Bar */}
                  <div className="h-12 bg-[#0B1F17] flex items-center justify-center border-b border-[#D4AF37]/10">
                    <div className="w-20 h-5 bg-black rounded-full" />
                  </div>
                  
                  {/* App Content - Heritage Theme */}
                  <div className="p-4 space-y-4">
                    {/* Greeting */}
                    <p className="font-body text-sm text-[#FAFAF7]/50">Good morning,</p>
                    <h3 className="font-heading text-xl font-bold text-[#FAFAF7]">
                      Marcus <span className="text-[#D4AF37]">🌱</span>
                    </h3>

                    {/* Financial Health Score - Glassmorphic Card */}
                    <div className="glass-heritage rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-body text-xs text-[#FAFAF7]/50 uppercase tracking-wider">Financial Health</span>
                        <span className="font-mono text-xs text-[#2ECC71] font-semibold">↑ 3 pts</span>
                      </div>
                      <div className="flex items-end gap-2">
                        <span className="font-mono text-4xl font-bold text-[#D4AF37]">74</span>
                        <span className="font-mono text-lg text-[#FAFAF7]/30 pb-1">/100</span>
                      </div>
                      {/* Progress Ring Visual */}
                      <div className="mt-3 h-2 bg-[#0B1F17] rounded-full overflow-hidden border border-[#D4AF37]/20">
                        <div className="h-full w-[74%] bg-gradient-to-r from-[#D4AF37] to-[#95B8A2] rounded-full" />
                      </div>
                    </div>

                    {/* Vera Insight Card */}
                    <div className="glass-heritage rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-full flex items-center justify-center text-sm">
                          🌿
                        </div>
                        <span className="font-body text-sm font-semibold text-[#D4AF37]">Vera says</span>
                      </div>
                      <p className="font-body text-sm text-[#FAFAF7]/80 leading-relaxed">
                        "You're 23% under your grocery budget this week! Keep it up — that's $47 toward your vacation fund."
                      </p>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="glass-heritage rounded-xl p-3">
                        <span className="font-body text-xs text-[#FAFAF7]/50 block mb-1">This Week</span>
                        <span className="font-mono text-lg font-bold text-[#FAFAF7]">$892</span>
                      </div>
                      <div className="glass-heritage rounded-xl p-3">
                        <span className="font-body text-xs text-[#FAFAF7]/50 block mb-1">Saved</span>
                        <span className="font-mono text-lg font-bold text-[#2ECC71]">$248</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative glow elements */}
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#D4AF37]/20 rounded-full blur-3xl animate-ambient-glow" />
              <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-[#95B8A2]/15 rounded-full blur-[50px] animate-ambient-glow" style={{ animationDelay: '1.5s' }} />
            </div>
          </div>
        </div>
        
        {/* TODO: Update hero mockup with new app design when screenshot is provided */}
      </div>
    </section>
  );
};

export default HeroSection;
